import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, RefreshCw, Plus } from "lucide-react";
import { useAlert } from "context/AlertContext";
import apiEmailDashboard from "./api";
import { RANGE_OPTIONS } from "./utils";
import StatTiles from "./widgets/StatTiles";
import TrendChart from "./widgets/TrendChart";
import DeliveryFunnel from "./widgets/DeliveryFunnel";
import CategoryBreakdown from "./widgets/CategoryBreakdown";
import TypeBreakdown from "./widgets/TypeBreakdown";
import RecipientStatusBreakdown from "./widgets/RecipientStatusBreakdown";
import RecentCampaigns from "./widgets/RecentCampaigns";
import QuotaCard from "./widgets/QuotaCard";
import SystemEmailStatTiles from "./widgets/SystemEmailStatTiles";
import SystemEmailTrendChart from "./widgets/SystemEmailTrendChart";
import SystemEmailTypeBreakdown from "./widgets/SystemEmailTypeBreakdown";

const EmailDashboard = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [range, setRange] = useState(30);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (selectedRange, isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await apiEmailDashboard.getOverview(selectedRange);
        setOverview(res.data?.data || null);
      } catch (err) {
        showAlert({ type: "error", message: err.response?.data?.message || "Failed to load dashboard data." });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [showAlert]
  );

  useEffect(() => {
    load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  return (
    <div className="h-full overflow-y-auto bg-background font-sans">
      <div className="bg-surface border-b border-border sticky top-0 z-20">
        <div className="max-w-[100rem] mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
              <Mail size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-text_primary leading-tight">Email Intelligence</h1>
              <p className="text-xs text-text_muted">Campaign & delivery analytics</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-xl border border-border bg-background p-0.5">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setRange(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                    range === opt.value ? "bg-primary text-white" : "text-text_secondary"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => load(range, true)}
              className={`p-2 rounded-xl border border-border bg-surface text-text_secondary ${refreshing ? "animate-spin" : ""}`}
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={() => navigate("/swiftboard/email-management/campaigns")}
              className="hidden sm:flex items-center gap-1.5 px-3.5 py-2 bg-primary text-white text-sm font-medium rounded-xl"
            >
              <Plus size={14} />
              New Campaign
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[100rem] mx-auto w-full py-3 space-y-5">
        <StatTiles summary={overview?.summary} deltas={overview?.deltas} loading={loading} />

        <div className="space-y-5">
          <SystemEmailTrendChart data={overview?.system_emails?.trend} loading={loading} rangeDays={range} />
          <TrendChart data={overview?.trend} loading={loading} rangeDays={range} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <DeliveryFunnel funnel={overview?.funnel} loading={loading} />
          <CategoryBreakdown data={overview?.category_breakdown} loading={loading} />
          <TypeBreakdown data={overview?.type_breakdown} loading={loading} />
          <RecipientStatusBreakdown data={overview?.recipient_status_breakdown} loading={loading} />
          <SystemEmailTypeBreakdown data={overview?.system_emails?.type_breakdown} loading={loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <RecentCampaigns campaigns={overview?.recent_campaigns} loading={loading} />
          <QuotaCard quota={overview?.quota} loading={loading} />
        </div>

        <div className="pt-2">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-sm font-semibold text-text_primary whitespace-nowrap">System Emails</h2>
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-text_muted whitespace-nowrap">Auth, notifications & alerts — separate from marketing campaigns</span>
          </div>

          <SystemEmailStatTiles summary={overview?.system_emails?.summary} deltas={overview?.system_emails?.deltas} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default EmailDashboard;
